import axios from 'axios';
import { isLocalPreview } from './previewMode';

function previewPath(url = '') {
  return new URL(url, 'https://preview.invalid').pathname
    .replace(/^\/api(?=\/|$)/, '')
    .replace(/\/$/, '') || '/';
}

function previewParams(url = '', params = {}) {
  const parsed = new URL(url, 'https://preview.invalid');
  const query = Object.fromEntries(parsed.searchParams.entries());
  return { ...query, ...(params || {}) };
}

function playerQuestPreview(quest) {
  return {
    id: quest.id || '',
    title: quest.title || 'Untitled quest',
    summary: quest.summary || '',
    hook: quest.hook || '',
    status: quest.status || 'available',
    is_pinned: Boolean(quest.is_pinned),
    updated_at: quest.updated_at || '',
    objectives: Array.isArray(quest.objectives) ? quest.objectives.map(objective => ({
      id: objective.id || '',
      title: objective.title || 'Untitled objective',
      status: objective.status || 'upcoming',
      optional: Boolean(objective.optional),
    })) : [],
  };
}

function dispatchPlayerQuestPreview({ method, url, previewRequest }) {
  if (method !== 'get') return { handled: false, data: null };
  const match = previewPath(url).match(/^\/player\/campaign\/([^/]+)\/quests$/);
  if (!match) return { handled: false, data: null };
  const campaignId = decodeURIComponent(match[1]);
  const quests = previewRequest('get', `/campaigns/${campaignId}/quests`);
  return {
    handled: true,
    data: (Array.isArray(quests) ? quests : [])
      .filter(quest => quest.shared_with_players === true && quest.status !== 'archived')
      .map(playerQuestPreview),
  };
}

async function dispatchCharacterProgressionPreview({ method, url, params, body, previewRequest }) {
  const path = previewPath(url);
  const match = path.match(/^\/characters\/([^/]+)\/(level-up-options|level-up|multiclass)$/);
  if (!match) return { handled: false, data: null };

  const [, rawCharacterId, action] = match;
  const characterId = decodeURIComponent(rawCharacterId);
  const character = previewRequest('get', `/characters/${characterId}`);
  const {
    applyPreviewCharacterLevelUp,
    getPreviewLevelUpOptions,
  } = await import('./previewCharacterProgression');

  if (action === 'level-up-options' && method === 'get') {
    const query = previewParams(url, params);
    return {
      handled: true,
      data: getPreviewLevelUpOptions(character, { targetClass: query.target_class || '' }),
    };
  }

  if (action === 'level-up' && method === 'post') {
    const updated = applyPreviewCharacterLevelUp(character, body, { multiclass: false });
    return { handled: true, data: previewRequest('patch', `/characters/${characterId}`, updated) };
  }

  if (action === 'multiclass' && method === 'post') {
    const updated = applyPreviewCharacterLevelUp(character, body, { multiclass: true });
    return { handled: true, data: previewRequest('patch', `/characters/${characterId}`, updated) };
  }

  return { handled: false, data: null };
}

async function dispatchStateSafeBuilderEdit({ method, url, body, previewRequest }) {
  if (method !== 'patch') return { handled: false, data: null };
  const match = previewPath(url).match(/^\/characters\/([^/]+)$/);
  if (!match) return { handled: false, data: null };

  const { isPreviewFullBuilderEdit, stateSafePreviewBuilderEdit } = await import('./previewCharacterEdit');
  if (!isPreviewFullBuilderEdit(body)) return { handled: false, data: null };

  const characterId = decodeURIComponent(match[1]);
  const existing = previewRequest('get', `/characters/${characterId}`);
  const safeUpdate = stateSafePreviewBuilderEdit(existing, body);
  return {
    handled: true,
    data: previewRequest('patch', `/characters/${characterId}`, safeUpdate),
  };
}

function guardIsolatedPreviewFeature(method, url) {
  const path = previewPath(url);
  if (method === 'post' && path === '/character-import/extract') {
    const error = new Error(
      'Automatic PDF/photo scanning is disabled in this isolated no-login preview because preview files never leave your browser. JSON, TXT, pasted text, and manual character import still work here. Use a signed-in test deployment to test real PDF/photo scanning.'
    );
    error.status = 400;
    throw error;
  }
}

// Every preview API call terminates locally. Unsupported routes fail explicitly;
// there is deliberately no fallback to a real API or shared account.
export async function previewAdapter(config) {
  const { previewRequest } = await import('./previewApi');
  try {
    const method = String(config.method || 'get').toLowerCase();
    guardIsolatedPreviewFeature(method, config.url);
    const body = typeof config.data === 'string' ? JSON.parse(config.data) : config.data || {};

    const playerQuests = dispatchPlayerQuestPreview({ method, url: config.url, previewRequest });
    if (playerQuests.handled) {
      return { data: playerQuests.data, status: 200, statusText: 'OK', headers: {}, config };
    }

    const safeEdit = await dispatchStateSafeBuilderEdit({ method, url: config.url, body, previewRequest });
    if (safeEdit.handled) {
      return { data: safeEdit.data, status: 200, statusText: 'OK', headers: {}, config };
    }

    const progression = await dispatchCharacterProgressionPreview({
      method,
      url: config.url,
      params: config.params,
      body,
      previewRequest,
    });
    const data = progression.handled
      ? progression.data
      : previewRequest(method, config.url, body);
    return { data, status: 200, statusText: 'OK', headers: {}, config };
  } catch (error) {
    const response = { data: { detail: error.message }, status: error.status || 400, statusText: 'Preview request failed', headers: {}, config };
    throw new axios.AxiosError(error.message, 'ERR_BAD_RESPONSE', config, null, response);
  }
}

export function installPreviewTransport(runtime = window) {
  if (!isLocalPreview(runtime.location.hostname, runtime)) return;
  // Set this before App and its legacy axios clients are evaluated.
  axios.defaults.adapter = previewAdapter;
  const originalFetch = runtime.fetch?.bind(runtime);
  if (!originalFetch) return;
  runtime.fetch = async (input, options = {}) => {
    const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, runtime.location.origin);
    if (!/^\/api(?:\/|$)/.test(url.pathname)) return originalFetch(input, options);
    const method = options.method || input?.method || 'GET';
    const body = options.body ?? (typeof input?.clone === 'function' ? await input.clone().text() : undefined);
    try {
      const response = await previewAdapter({ url: url.href, method, data: body });
      return new runtime.Response(JSON.stringify(response.data), { status: response.status, headers: { 'Content-Type': 'application/json' } });
    } catch (error) {
      return new runtime.Response(JSON.stringify(error.response?.data || { detail: error.message }), { status: error.response?.status || 400, headers: { 'Content-Type': 'application/json' } });
    }
  };
}
